/** Manual availability list signup. No inventory-triggered notification is created. */
class AvailabilityWaitlist extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    this.form = this.querySelector('[data-waitlist-form]');
    this.submitButton = this.querySelector('[data-waitlist-submit]');
    this.success = this.querySelector('[data-waitlist-success]');
    this.error = this.querySelector('[data-waitlist-error]');
    this.variants = JSON.parse(this.querySelector('[data-waitlist-variants]').textContent);
    this.productForm = document.getElementById(this.dataset.productForm);
    this.variantId = this.productForm?.elements.namedItem('id')?.value || this.variantId || this.dataset.variantId;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.subscribe();
    }, { signal: this.listeners.signal });
    this.productForm?.addEventListener('variant:change', (event) => {
      if (event.detail?.variant) this.variantId = String(event.detail.variant.id);
      this.form.hidden = false;
      this.success.hidden = true;
      this.error.hidden = true;
    }, { signal: this.listeners.signal });
  }

  disconnectedCallback() {
    this.listeners?.abort();
  }

  async subscribe() {
    if (this.pending || !this.form.reportValidity()) return;
    const email = this.form.elements.email.value.trim();
    // Read at submit time so the selected variant stays correct after plan changes
    // and in quick-buy drawers. Fall back for product cards without a product form.
    const variantId = String(this.productForm?.elements.namedItem('id')?.value || this.variantId);
    const variant = this.variants[variantId];
    if (!this.dataset.companyId || !this.dataset.listId || !variant) {
      this.error.hidden = false;
      return;
    }
    // Stable product + country keys preserve interest in other products/markets.
    // A repeat signup refreshes only this product/country's details.
    const prefix = `waitlist_${this.dataset.productId}_${this.dataset.country}`;
    const properties = {
      [prefix]: variant.regionBlocked ? 'market_availability' : 'restock',
      [`${prefix}_product`]: this.dataset.productHandle,
      [`${prefix}_market_id`]: this.dataset.marketId,
      [`${prefix}_market`]: this.dataset.marketHandle,
      [`${prefix}_variant_id`]: variantId,
      [`${prefix}_variant`]: variant.title,
      [`${prefix}_locale`]: this.dataset.locale,
      [`${prefix}_requested_at`]: new Date().toISOString()
    };
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Product availability waitlist',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: this.dataset.listId } } }
      }
    };
    this.pending = true;
    this.error.hidden = true;
    this.submitButton.disabled = true;
    this.form.setAttribute('aria-busy', 'true');
    const label = this.submitButton.textContent;
    this.submitButton.textContent = this.dataset.submittingLabel;
    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(15000)
      });
      if (!response.ok) throw new Error('Subscription was not accepted');
      this.form.hidden = true;
      this.success.hidden = false;
      this.success.focus();
    } catch {
      // Keep the email available for retry; never report success for a failed request.
      this.error.hidden = false;
    } finally {
      this.pending = false;
      this.submitButton.disabled = false;
      this.submitButton.textContent = label;
      this.form.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('availability-waitlist')) {
  customElements.define('availability-waitlist', AvailabilityWaitlist);
}
